import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Target, Key, Users, CheckSquare, Layers } from 'lucide-react';
import { toast } from 'sonner';

function ExerciseManager({ token }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newExercise, setNewExercise] = useState({
    chapter_id: 1,
    exercise_number: '1',
    page_reference: '',
    title: '',
    exercise_type: 'four_choice',
    instructions: '',
    content: '',
    scenarios: [],
    answer_key: '',
    teacher_notes: ''
  });

  const exerciseTypes = [
    { value: 'four_choice', label: 'Four Basic Choices', icon: Target, description: 'Four response options exercise' },
    { value: 'matching', label: 'Matching Exercise', icon: CheckSquare, description: 'Match patterns or responses' },
    { value: 'identification', label: 'Pattern Identification', icon: Users, description: 'Identify response patterns' },
    { value: 'four_ds', label: 'Four D\'s Framework', icon: Layers, description: 'Diagnosis, Denial, Demand, Deserve' },
    { value: 'ofnr_practice', label: 'OFNR Practice', icon: Target, description: 'Full OFNR scenarios' }
  ];

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/exercises`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExercises(data.exercises || []);
      }
    } catch (error) {
      console.error('Failed to fetch exercises');
    } finally {
      setLoading(false);
    }
  };

  const createExercise = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/exercises`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newExercise,
          scenarios: newExercise.scenarios.filter(s => s.trim())
        })
      });

      if (!response.ok) throw new Error('Creation failed');

      toast.success('Interactive exercise created! Students can now practice this in their chapters. 🎯');
      setNewExercise({
        chapter_id: 1,
        exercise_number: '1',
        page_reference: '',
        title: '',
        exercise_type: 'four_choice',
        instructions: '',
        content: '',
        scenarios: [],
        answer_key: '',
        teacher_notes: ''
      });
      fetchExercises();
    } catch (error) {
      toast.error('Failed to create exercise');
    }
  };

  const addScenario = () => {
    setNewExercise(prev => ({
      ...prev,
      scenarios: [...prev.scenarios, '']
    }));
  };

  const updateScenario = (index, value) => {
    setNewExercise(prev => ({
      ...prev,
      scenarios: prev.scenarios.map((s, i) => i === index ? value : s)
    }));
  };

  return (
    <div className="space-y-6">
      
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">🎯 Interactive Exercise Manager</h3>
          <p className="text-gray-600">Create exercises for pages 89, 90, 91-92 and other PDF content</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 text-white hover:bg-blue-700" style={{ borderRadius: 0 }}>
              <Plus className="w-4 h-4 mr-2" />
              Create Exercise
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 0 }}>
            <DialogHeader>
              <DialogTitle>Create Interactive Exercise</DialogTitle>
            </DialogHeader>
            <form onSubmit={createExercise} className="space-y-4">
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block font-bold mb-2">Chapter</label>
                  <select
                    value={newExercise.chapter_id}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, chapter_id: parseInt(e.target.value) }))}
                    className="w-full p-3 border-2 border-black"
                    style={{ borderRadius: 0 }}
                  >
                    {[1,2,3,4,5,6,7,8].map(num => (
                      <option key={num} value={num}>Chapter {num}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-bold mb-2">Exercise #</label>
                  <Input
                    value={newExercise.exercise_number}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, exercise_number: e.target.value }))}
                    placeholder="1, 2, 3"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold mb-2">Page Reference</label>
                  <Input
                    value={newExercise.page_reference}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, page_reference: e.target.value }))}
                    placeholder="89, 90, 91-92"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold mb-2">Exercise Type</label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {exerciseTypes.map(type => {
                    const IconComponent = type.icon;
                    return (
                      <Button
                        key={type.value}
                        type="button"
                        onClick={() => setNewExercise(prev => ({ ...prev, exercise_type: type.value }))}
                        className={`p-4 ${newExercise.exercise_type === type.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'} hover:opacity-90`}
                        style={{ borderRadius: 0 }}
                      >
                        <div className="text-center">
                          <IconComponent className="w-5 h-5 mx-auto mb-1" />
                          <p className="text-sm font-bold">{type.label}</p>
                          <p className="text-xs">{type.description}</p>
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              <div>
                <label className="block font-bold mb-2">Title</label>
                <Input
                  value={newExercise.title}
                  onChange={(e) => setNewExercise(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Four Basic Choices Exercise (Page 91-92)"
                  className="border-black"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>
              
              <div>
                <label className="block font-bold mb-2">Instructions</label>
                <Textarea
                  value={newExercise.instructions}
                  onChange={(e) => setNewExercise(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Copy exercise instructions from PDF"
                  className="border-black min-h-[80px]"
                  style={{ borderRadius: 0 }}
                  required
                />
              </div>
              
              <div>
                <label className="block font-bold mb-2">Exercise Content (Paste from PDF)</label>
                <div className="space-y-3">
                  <Textarea
                    value={newExercise.content}
                    onChange={(e) => setNewExercise(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Paste the complete exercise content from pages 89, 90, 91-92, etc."
                    className="border-black min-h-[200px]"
                    style={{ borderRadius: 0 }}
                    required
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!newExercise.content.trim()) {
                        toast.error('Please paste content first');
                        return;
                      }
                      
                      try {
                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/format-content`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            raw_content: newExercise.content,
                            content_type: 'homework_assignment',
                            chapter: newExercise.chapter_id
                          })
                        });
                        
                        if (response.ok) {
                          const data = await response.json();
                          setNewExercise(prev => ({ ...prev, content: data.formatted_content }));
                          toast.success('Content reformatted for better readability! ✨');
                        } else {
                          toast.error('Reformatting failed');
                        }
                      } catch (error) {
                        toast.error('Reformatting error');
                      }
                    }}
                    className="bg-purple-600 text-white hover:bg-purple-700"
                    style={{ borderRadius: 0 }}
                  >
                    ✨ AI Format for Readability
                  </Button>
                </div>
              </div>

              <div>
                <label className="block font-bold mb-2">Scenarios/Questions</label>
                <div className="space-y-2">
                  {newExercise.scenarios.map((scenario, index) => (
                    <Input
                      key={index}
                      value={scenario}
                      onChange={(e) => updateScenario(index, e.target.value)}
                      placeholder={`Scenario ${index + 1}`}
                      className="border-black"
                      style={{ borderRadius: 0 }}
                    />
                  ))}
                  <Button
                    type="button"
                    onClick={addScenario}
                    variant="outline"
                    className="border-black"
                    style={{ borderRadius: 0 }}
                  >
                    + Add Scenario
                  </Button>
                </div>
              </div>

              <div>
                <label className="block font-bold mb-2">Answer Key (Optional)</label>
                <Textarea
                  value={newExercise.answer_key}
                  onChange={(e) => setNewExercise(prev => ({ ...prev, answer_key: e.target.value }))}
                  placeholder="Paste answer key from PDF (page 92, etc.)"
                  className="border-black min-h-[100px]"
                  style={{ borderRadius: 0 }}
                />
              </div>

              <div>
                <label className="block font-bold mb-2">Teacher's Notes</label>
                <Textarea
                  value={newExercise.teacher_notes}
                  onChange={(e) => setNewExercise(prev => ({ ...prev, teacher_notes: e.target.value }))}
                  placeholder="Special notes for instructors using this exercise"
                  className="border-black min-h-[80px]"
                  style={{ borderRadius: 0 }}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4"
                style={{ borderRadius: 0 }}
              >
                Create Interactive Exercise
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Templates for Specific Pages */}
      <Card className="border-4 border-green-500 bg-green-50" style={{ borderRadius: 0 }}>
        <CardHeader>
          <CardTitle className="text-xl text-green-800">🎯 Chapter 1 Exercise Templates</CardTitle>
          <p className="text-green-700">Quick setup for pages 89, 90, 91-92</p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button
              onClick={() => {
                setNewExercise(prev => ({
                  ...prev,
                  chapter_id: 1,
                  exercise_number: '1',
                  page_reference: '89',
                  title: 'Using OFNR in Honesty and Empathy (Page 89)',
                  exercise_type: 'ofnr_practice',
                  instructions: 'Paste instructions from page 89...',
                  content: 'Paste complete exercise content from page 89...'
                }));
                toast.success('Page 89 template ready! Paste PDF content.');
              }}
              className="bg-blue-600 text-white hover:bg-blue-700 py-6"
              style={{ borderRadius: 0 }}
            >
              📖 Page 89<br/>Exercise 1
            </Button>
            <Button
              onClick={() => {
                setNewExercise(prev => ({
                  ...prev,
                  chapter_id: 1,
                  exercise_number: 'Four Ds',
                  page_reference: '90',
                  title: 'The Four D\'s of Disconnection (Page 90)',
                  exercise_type: 'four_ds',
                  instructions: 'Paste Four D\'s exercise from page 90...',
                  content: 'Paste complete Four D\'s content...'
                }));
                toast.success('Page 90 Four D\'s template ready!');
              }}
              className="bg-red-600 text-white hover:bg-red-700 py-6"
              style={{ borderRadius: 0 }}
            >
              🎯 Page 90<br/>Four D's
            </Button>
            <Button
              onClick={() => {
                setNewExercise(prev => ({
                  ...prev,
                  chapter_id: 1,
                  exercise_number: '2-3',
                  page_reference: '91-92',
                  title: 'Exercises 2 & 3 (Pages 91-92)',
                  exercise_type: 'matching',
                  instructions: 'Paste exercises 2 and 3 instructions...',
                  content: 'Paste complete exercises 2 & 3 content with answer key...'
                }));
                toast.success('Pages 91-92 template ready!');
              }}
              className="bg-green-600 text-white hover:bg-green-700 py-6"
              style={{ borderRadius: 0 }}
            >
              ✅ Pages 91-92<br/>Exercises 2 & 3
            </Button>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-500">
            <h4 className="font-bold text-yellow-800 mb-2">📋 How to Add PDF Exercises:</h4>
            <ol className="text-sm text-yellow-700 space-y-1">
              <li>1. Click a template button above (or "Create Exercise")</li>
              <li>2. Copy exercise content directly from PDF pages</li>
              <li>3. Paste into "Exercise Content" field</li>
              <li>4. Add scenarios, answer keys, teacher notes</li>
              <li>5. Students will see interactive exercises in chapters!</li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Exercises by Chapter */}
      <div className="space-y-6">
        {[1,2,3,4,5,6,7,8].map(chapterNum => {
          const chapterExercises = exercises.filter(e => e.chapter_id === chapterNum);
          return (
            <Card key={chapterNum} style={{ borderRadius: 0 }} className="border-2 border-blue-500">
              <CardHeader>
                <CardTitle className="text-xl text-blue-800">
                  Chapter {chapterNum} - Interactive Exercises ({chapterExercises.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chapterExercises.length === 0 ? (
                  <p className="text-gray-500 italic py-6 text-center">
                    No interactive exercises yet. Use templates above to add PDF exercises.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {chapterExercises.map((exercise) => (
                      <Card key={exercise.id} className="border border-gray-300" style={{ borderRadius: 0 }}>
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-bold text-gray-800 mb-1">
                                Exercise {exercise.exercise_number}: {exercise.title}
                              </h4>
                              <div className="flex gap-2 mb-2">
                                <Badge className="bg-blue-500 text-white">Page {exercise.page_reference}</Badge>
                                <Badge variant="outline">{exerciseTypes.find(t => t.value === exercise.exercise_type)?.label}</Badge>
                                {exercise.has_answer_key && <Badge className="bg-green-500 text-white">Answer Key</Badge>}
                                {exercise.has_teacher_notes && <Badge className="bg-purple-500 text-white">Teacher Notes</Badge>}
                              </div>
                              <p className="text-sm text-gray-700">{exercise.content}</p>
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(exercise.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

    </div>
  );
}

export default ExerciseManager;